import * as React from "react"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

/* ── Variants ─────────────────────────────────────────────── */

const dialogOverlayVariants = cva([
  "fixed inset-0 z-50 bg-coval-overlay",
  "data-[state=open]:animate-in data-[state=open]:fade-in-0",
  "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
])

const dialogContentVariants = cva(
  [
    "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
    "flex flex-col rounded-lg",
    "bg-surface-default text-coval-text shadow-lg",
    "border border-coval-border",
    "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
  ],
  {
    variants: {
      size: {
        sm: "w-full max-w-sm",
        md: "w-full max-w-lg",
        lg: "w-full max-w-2xl",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
)

/* ── Root ──────────────────────────────────────────────────── */

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogClose = DialogPrimitive.Close

/* ── Overlay ──────────────────────────────────────────────── */

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(dialogOverlayVariants(), className)}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/* ── Content ──────────────────────────────────────────────── */

export interface DialogContentProps
  extends
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  /**
   * Extra classes for the dimming overlay. The overlay is rendered here rather
   * than by the caller, so raising the content out of the default layer needs
   * this to bring the dimming with it.
   */
  overlayClassName?: string
}

const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      overlayClassName,
      size,
      children,
      onOpenAutoFocus,
      onCloseAutoFocus,
      ...props
    },
    ref,
  ) => {
    // Radix returns focus to the DialogTrigger, which is empty when the dialog
    // is opened from state. Record what had focus as the dialog opens and
    // return focus there however it closes; when that element has left the
    // page, Radix's trigger fallback runs. A caller's handler that prevents
    // the default keeps full control of focus.
    const returnFocusRef = React.useRef<HTMLElement | null>(null)

    return (
      <DialogPrimitive.Portal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(dialogContentVariants({ size }), className)}
          onOpenAutoFocus={(event) => {
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement &&
              document.activeElement !== document.body
                ? document.activeElement
                : null
            onOpenAutoFocus?.(event)
          }}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event)
            const returnFocus = returnFocusRef.current
            returnFocusRef.current = null
            if (event.defaultPrevented || !returnFocus?.isConnected) {
              return
            }
            event.preventDefault()
            returnFocus.focus()
          }}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    )
  },
)
DialogContent.displayName = DialogPrimitive.Content.displayName

/* ── Header ───────────────────────────────────────────────── */

const DialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 px-4 pt-4 pb-0", className)}
    {...props}
  />
))
DialogHeader.displayName = "DialogHeader"

/* ── Body ─────────────────────────────────────────────────── */

const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex-1 overflow-y-auto px-4 py-3", className)}
    {...props}
  />
))
DialogBody.displayName = "DialogBody"

/* ── Footer ───────────────────────────────────────────────── */

const DialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-end gap-2 px-4 pt-0 pb-4",
      className,
    )}
    {...props}
  />
))
DialogFooter.displayName = "DialogFooter"

/* ── Title ────────────────────────────────────────────────── */

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-sm font-semibold text-coval-text", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

/* ── Description ──────────────────────────────────────────── */

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-coval-text-muted", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

/* ── Exports ──────────────────────────────────────────────── */

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  dialogOverlayVariants,
  dialogContentVariants,
}
